import { Button, FormField, Select, Skeleton } from "@design";
import type { AnyFieldApi } from "@tanstack/react-form";
import { useQuery } from "@tanstack/react-query";
import { FileUp } from "lucide-react";
import { useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import { listSpaceKnowledgeOptions } from "@/api";
import { Text } from "@/components/Text";

// What the backend can read (KG import; images and audio aren't supported).
const ACCEPT = ".pdf,.docx,.doc,.xlsx,.xls,.csv,.md,.markdown,.txt,.html,.htm,.pptx,.ppt,.json";
const IMPORTABLE = new Set(ACCEPT.split(","));

function fieldError(field: AnyFieldApi): string | undefined {
  const first = field.state.meta.errors[0] as unknown;
  if (!first) return undefined;
  return typeof first === "string" ? first : String((first as { message?: string }).message ?? "");
}

/** The picker inside FormField: takes FormField's `invalid` / `aria-describedby` instead of leaking them to a div. */
function FilePicker({
  id,
  file,
  onChoose,
  invalid,
  "aria-describedby": describedBy,
}: Readonly<{ id: string; file: File | null; onChoose: () => void; invalid?: boolean; "aria-describedby"?: string }>) {
  const { t } = useTranslation("knowledge");
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          id={id}
          variant="secondary"
          size="sm"
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          leadingIcon={<FileUp size={16} aria-hidden />}
          onClick={onChoose}
        >
          {t("import.chooseFile")}
        </Button>
        <Text as="span" variant="body-sm" tone={file ? "primary" : "tertiary"} className="min-w-0 break-all">
          {file ? file.name : t("import.noFile")}
        </Text>
      </div>
      <Text variant="caption" tone="tertiary">
        {t("import.fileHint")}
      </Text>
    </div>
  );
}

/** Choose a file to upload (kept in the form; the upload happens on submit). */
export function ImportFileField({ field }: Readonly<{ field: AnyFieldApi }>) {
  const { t } = useTranslation("knowledge");
  const input = useRef<HTMLInputElement>(null);
  const id = useId();
  return (
    <>
      <FormField label={t("import.sources.upload")} htmlFor={id} required error={fieldError(field)}>
        <FilePicker id={id} file={field.state.value as File | null} onChoose={() => input.current?.click()} />
      </FormField>
      <input
        ref={input}
        type="file"
        accept={ACCEPT}
        hidden
        aria-label={t("import.chooseFile")}
        onChange={(event) => {
          field.handleChange(event.target.files?.[0] ?? null);
          event.target.value = "";
        }}
      />
    </>
  );
}

function importable(filename: string): boolean {
  const dot = filename.lastIndexOf(".");
  return dot >= 0 && IMPORTABLE.has(filename.slice(dot).toLowerCase());
}

/** Pick one of the space's knowledge-base files. */
export function ImportSpaceFileField({ spaceUuid, field }: Readonly<{ spaceUuid: string; field: AnyFieldApi }>) {
  const { t } = useTranslation("knowledge");
  const id = useId();
  const files = useQuery({
    ...listSpaceKnowledgeOptions({ path: { space_uuid: spaceUuid } }),
    select: (r) => r.data.filter((f) => importable(f.filename)),
  });
  if (files.isPending) return <Skeleton className="h-10 w-full" />;
  if (files.isError) {
    return (
      <Text role="alert" tone="error">
        {t("import.spaceFilesFailed")}
      </Text>
    );
  }
  if (files.data.length === 0) {
    const error = fieldError(field);
    return (
      <div className="flex flex-col gap-1">
        <Text tone="secondary">{t("import.spaceFilesEmpty")}</Text>
        {error && (
          <Text role="alert" variant="body-sm" tone="error">
            {error}
          </Text>
        )}
      </div>
    );
  }
  return (
    <FormField label={t("import.spaceFile")} htmlFor={id} required error={fieldError(field)}>
      <Select
        id={id}
        aria-label={t("import.spaceFile")}
        options={files.data.map((f) => ({ value: f.uuid, label: f.filename }))}
        placeholder={t("import.spaceFileRequired")}
        value={(field.state.value as string) || undefined}
        onChange={(value) => field.handleChange(value)}
      />
    </FormField>
  );
}
